import { Fragment, type ReactNode } from "react";
import { safeExternalHttpUrl } from "@business-ops/domain";

export type SafeLinkedTextSegment =
  | { kind: "TEXT"; text: string }
  | { kind: "LINK"; text: string; href: string };

const URL_CANDIDATE = /\b(?:https?:\/\/|www\.)[^\s<>"'`]+/giu;
const SIMPLE_TRAILING_PUNCTUATION = new Set([".", ",", "!", "?", ";", ":"]);
const CLOSING_PAIRS = [[")", "("], ["]", "["], ["}", "{"]] as const;

/**
 * WHY [BOL-SAFE-CLICKABLE-REFERENCE-001]: WhatsApp, Work, and Agent text is
 * untrusted evidence, but operators must be able to open a referenced Drive or
 * web URL without copying it by hand. React continues to escape all text; only
 * candidates that the platform URL parser accepts as HTTP(S) become anchors.
 * Never render provider text as HTML or accept another scheme. Falsifier: a
 * normal HTTP(S) reference stays inert, punctuation enters the destination, or
 * message text can create script/data/file navigation.
 */
export function safeLinkedTextSegments(text: string): SafeLinkedTextSegment[] {
  const segments: SafeLinkedTextSegment[] = [];
  let cursor = 0;
  for (const match of text.matchAll(URL_CANDIDATE)) {
    const start = match.index ?? 0;
    if (start > cursor) segments.push({ kind: "TEXT", text: text.slice(cursor, start) });
    const candidate = match[0];
    const linkText = trimTrailingPunctuation(candidate);
    const trailing = candidate.slice(linkText.length);
    const href = safeHttpHref(linkText);
    if (href) segments.push({ kind: "LINK", text: linkText, href });
    else segments.push({ kind: "TEXT", text: linkText });
    if (trailing) segments.push({ kind: "TEXT", text: trailing });
    cursor = start + candidate.length;
  }
  if (cursor < text.length) segments.push({ kind: "TEXT", text: text.slice(cursor) });
  return segments.length ? segments : [{ kind: "TEXT", text }];
}

export function SafeLinkedText({ text }: { text: string }): ReactNode {
  return <>{safeLinkedTextSegments(text).map((segment, index) => segment.kind === "LINK"
    ? <a className="safe-external-link" href={segment.href} target="_blank" rel="noopener noreferrer" key={`${index}:${segment.href}`}>{segment.text}</a>
    : <Fragment key={`${index}:${segment.text}`}>{segment.text}</Fragment>)}</>;
}

function safeHttpHref(value: string): string | null {
  try {
    const parsed = new URL(value.startsWith("www.") ? `https://${value}` : value);
    // WHY [BOL-SAFE-CLICKABLE-CREDENTIALS-001]: URL credentials are invisible
    // in much of the rendered anchor but still leave the app on click. Reuse
    // the typed-reference trust boundary after normalizing `www.` instead of
    // maintaining a looser second URL policy for untrusted message text.
    // Falsifier: user:password@host becomes clickable, or a normal HTTP(S)
    // reference accepted by safeExternalHttpUrl stays inert.
    return safeExternalHttpUrl(parsed.href) === null ? null : parsed.href;
  } catch {
    return null;
  }
}

function trimTrailingPunctuation(value: string): string {
  let end = value.length;
  while (end > 0 && SIMPLE_TRAILING_PUNCTUATION.has(value[end - 1]!)) end -= 1;
  let changed = true;
  while (changed && end > 0) {
    changed = false;
    const current = value.slice(0, end);
    for (const [closing, opening] of CLOSING_PAIRS) {
      if (!current.endsWith(closing)) continue;
      if (count(current, closing) <= count(current, opening)) continue;
      end -= 1;
      changed = true;
      break;
    }
  }
  return value.slice(0, end);
}

function count(value: string, needle: string): number {
  return value.split(needle).length - 1;
}
